import type { DecodedArray, SchemaElement, SchemaTree } from "hyparquet";
import { isListLike, isMapLike } from "hyparquet/src/schema.js";
import { ParquetWriter as HyparquetWriter } from "hyparquet-writer";
import sortedIndexBy from "lodash/sortedIndexBy";

import { type TypedArray, combineArrays } from "@/shared/array";

import { OPFSFileWriter } from "./OPFSFileWriter";
import type { Value } from "./ReaderLike";
import { mapKeyParser } from "./schema";

export interface ParquetWriterOptions {
  /**
   * Whether to use compression.
   * Defaults to `false` to optimize for reads.
   */
  compressed: boolean;

  offset: number;

  /**
   * Size of each row group in the parquet file.
   * Use `ParquetWriter::calculateTargetRowGroupSize` to calculate a suitable value.
   * Defaults to 1_000 to optimize for reading small slices of data at a time without overfetching.
   */
  rowGroupSize: number;

  /**
   * Whether to calculate and write statistics for each column for each row group.
   * Defaults to `true`.
   */
  statistics: boolean;
}

interface QueuedWrite {
  data: Value[];
  rowStart: number;
  rowEnd: number;
}

export class ParquetWriter {
  private closed = false;
  private columnDef: SchemaElement[];
  private fileWriter: OPFSFileWriter;
  private nextRowStart: number;
  private rowGroupSize: number;
  private writer: HyparquetWriter;
  private writeQueue: QueuedWrite[];

  static create(
    fileHandle: FileSystemSyncAccessHandle,
    columnDef: SchemaElement | SchemaElement[],
    options?: Partial<ParquetWriterOptions>,
  ): ParquetWriter {
    return new ParquetWriter(fileHandle, columnDef, options);
  }

  static calculateTargetRowGroupSize(
    numRows: number,
    /**
     * More row groups allows for more efficient filtering, but increases the cost of reading the file's metadata.
     */
    maxRowGroupCount: number = 100,
    /**
     * Smaller row groups allow for finer-grained reads.
     */
    minRowGroupSize: number = 1_000,
  ): number {
    const maxRowGroupSize = Math.ceil(numRows / maxRowGroupCount);
    return Math.max(minRowGroupSize, maxRowGroupSize);
  }

  private constructor(
    fileHandle: FileSystemSyncAccessHandle,
    columnDef: SchemaElement | SchemaElement[],
    options?: Partial<ParquetWriterOptions>,
  ) {
    this.fileWriter = new OPFSFileWriter(fileHandle);
    this.nextRowStart = options?.offset ?? 0;
    this.rowGroupSize = options?.rowGroupSize ?? 1000;

    // Nested schemas are represented as a flat list of SchemaElements.
    // If the column to write is not part of a nested schema,
    // it can either be specified as a `SchemaElement` or as a single element array of `SchemaElement`.
    this.columnDef = Array.isArray(columnDef) ? columnDef : [columnDef];
    if (this.columnDef.length === 0) {
      throw new Error(
        "ParquetWriter requires a schema for the column to write.",
      );
    }

    this.writeQueue = [];

    const schema: SchemaElement[] = [
      {
        repetition_type: "REQUIRED",
        name: "schema",
        // Schema is a flat pre-order array: each parent is followed immediately
        // by the full subtree of each direct child (parent, child0 subtree, child1 subtree, ...).
        //
        // num_children is the number of immediate/direct children only (not the size of the whole subtree).
        // For the root, set num_children to the number of top-level fields.
        //
        // LIST/MAP use wrapper nodes (e.g. LIST -> repeated node -> element)
        //
        // Assume this is a single-column writer
        num_children: 1,
      },
      ...this.columnDef,
    ];

    this.writer = new HyparquetWriter({
      writer: this.fileWriter,
      schema,
      codec: options?.compressed ? "SNAPPY" : "UNCOMPRESSED",
      statistics: options?.statistics ?? true,
    });
  }

  public cancel(): void {
    if (this.closed === true) {
      return;
    }

    this.fileWriter.cancel();

    this.closed = true;
  }

  public close(): void {
    if (this.closed === true) {
      return;
    }

    this.flushWriteQueue();

    // Ensure any remaining data is written in a final row group
    this.fileWriter.flush();

    // Set chunk size to infinite to write metadata in one go
    this.fileWriter.chunkSize = Number.POSITIVE_INFINITY;
    this.writer.finish(); // will call `this.fileWriter.finish()`
    this.closed = true;
  }

  public queueWrite(data: Value[], rowStart: number, rowEnd: number): void {
    const item = { data, rowStart, rowEnd };
    const index = sortedIndexBy(this.writeQueue, item, (item) => item.rowStart);
    this.writeQueue.splice(index, 0, item);
    this.maybeFlushWriteQueue();
  }

  private flushWriteQueue(): void {
    if (this.writeQueue.length === 0) {
      return;
    }

    const chunks = [];
    while (this.writeQueue.length > 0) {
      const chunk = this.writeQueue.shift();
      if (chunk === undefined) {
        break;
      }
      chunks.push(chunk);
    }

    const data = combineArrays(...chunks.map((chunk) => chunk.data));
    this.write(data);
  }

  private maybeFlushWriteQueue() {
    const toWrite = this.topQueuedWriteItems();
    if (toWrite === 0) {
      return;
    }

    const chunks = [];
    for (let i = 0; i < toWrite; i++) {
      const chunk = this.writeQueue.shift()!;
      chunks.push(chunk);
    }

    const data = combineArrays(...chunks.map((chunk) => chunk.data));
    this.write(data);
  }

  private topQueuedWriteItems(): number {
    if (
      this.writeQueue.length === 0 ||
      this.writeQueue[0].rowStart !== this.nextRowStart
    ) {
      return 0;
    }

    let itemsToTake = 0;
    let lastRow = this.nextRowStart - 1;
    let rowCountQueued = 0;

    for (const item of this.writeQueue) {
      if (item.rowStart - lastRow > 1) {
        // Found a gap, so we stop.
        break;
      }

      itemsToTake++;
      lastRow = item.rowEnd;
      rowCountQueued += item.rowEnd - item.rowStart + 1;

      if (rowCountQueued >= this.rowGroupSize) {
        return itemsToTake;
      }
    }

    // If we have a continuous block of data from the start of the queue,
    // but it's less than a full row group, we can still decide to write it.
    // For now, let's only write when we have a full row group.
    return 0;
  }

  private write(data: Value[] | TypedArray): void {
    const name = this.columnDef.at(0)?.name;
    if (name === undefined) {
      throw new Error("ParquetWriter requires a column name to write.");
    }
    this.writer.write({
      columnData: [
        {
          name,
          data: data as DecodedArray,
        },
      ],
      rowGroupSize: this.rowGroupSize,
    });
    this.nextRowStart += data.length;
  }
}

/**
 * The schema a cached part stores the source column `columnTree` under, in
 * the flat pre-order form hyparquet-writer takes. It is the source's schema
 * with the changes hyparquet-writer needs, each of which reads back as the
 * same Arrow type:
 *
 * - INT96, which hyparquet-writer cannot write, becomes INT64 annotated
 *   TIMESTAMP(NANOS) not adjusted to UTC: the nanoseconds hyparquet reads an
 *   INT96 as.
 * - A list whose repeated field is the element, which hyparquet-writer
 *   cannot write (a repeated field outside a LIST group, or a LIST whose
 *   repeated field is a primitive or a group of several fields), is written
 *   in the three-level form, its repeated field becoming the required
 *   element.
 * - A DECIMAL or LIST annotated only with a logical type also gets its
 *   converted type, and so does a MAP, including a group annotated
 *   MAP_KEY_VALUE in place of MAP: hyparquet-writer's own copy of hyparquet
 *   recognizes these by converted type only.
 * - A map's key and value fields are named `key` and `value`, since
 *   hyparquet-writer turns each map entry into a `{ key, value }` object and
 *   reads each field from it by the field's name.
 */
export function cachedPartSchema(columnTree: SchemaTree): SchemaElement[] {
  const elements: SchemaElement[] = [];
  appendPartElements(columnTree, columnTree.element.name, elements);
  return elements;
}

/**
 * The function that turns a chunk of the source column's values, as a
 * reader assembles them, into what hyparquet-writer takes for
 * {@link cachedPartSchema}, or undefined when no value needs a change: a
 * list whose repeated field is the element gets the values of its
 * three-level form, and a map, which hyparquet assembles as an object keyed
 * by each key's string form, gets keys of its key field's type.
 */
export function cachedPartValues(
  columnTree: SchemaTree,
): ((values: Value[]) => Value[]) | undefined {
  const rewrite = partValue(columnTree);
  return rewrite === undefined
    ? undefined
    : (values) => Array.from(values, (value) => rewrite(value) as Value);
}

type Rewrite = (value: unknown) => unknown;

/**
 * Whether `node` is a LIST group whose repeated field is the element, being
 * a primitive or a group of several fields, which hyparquet-writer cannot
 * write as is.
 */
function isListWhoseRepeatedFieldIsElement(node: SchemaTree): boolean {
  if (!isListAnnotated(node.element) || node.children.length !== 1) {
    return false;
  }
  const [repeated] = node.children;
  return (
    repeated.element.repetition_type === "REPEATED" &&
    repeated.children.length !== 1
  );
}

function isListAnnotated(element: SchemaElement): boolean {
  return (
    element.converted_type === "LIST" || element.logical_type?.type === "LIST"
  );
}

function appendPartElements(
  node: SchemaTree,
  name: string,
  elements: SchemaElement[],
): void {
  if (node.element.repetition_type === "REPEATED") {
    // A repeated field outside a LIST or MAP group: a required list of
    // required elements.
    elements.push(
      {
        name,
        repetition_type: "REQUIRED",
        converted_type: "LIST",
        num_children: 1,
      },
      { name: "list", repetition_type: "REPEATED", num_children: 1 },
    );
    appendUnrepeated(node, "element", "REQUIRED", elements);
    return;
  }
  appendUnrepeated(node, name, node.element.repetition_type, elements);
}

function appendUnrepeated(
  node: SchemaTree,
  name: string,
  repetition: SchemaElement["repetition_type"],
  elements: SchemaElement[],
): void {
  elements.push(partElement(node, name, repetition));
  if (isMapLike(node)) {
    const [entries] = node.children;
    const [key, value] = entries.children;
    elements.push(partElement(entries, entries.element.name, "REPEATED"));
    appendPartElements(key, "key", elements);
    appendPartElements(value, "value", elements);
    return;
  }
  if (isListWhoseRepeatedFieldIsElement(node)) {
    const [repeated] = node.children;
    elements.push({
      name: "list",
      repetition_type: "REPEATED",
      num_children: 1,
    });
    appendUnrepeated(repeated, "element", "REQUIRED", elements);
    return;
  }
  if (isListLike(node)) {
    const [repeated] = node.children;
    elements.push(partElement(repeated, repeated.element.name, "REPEATED"));
    for (const child of repeated.children) {
      appendPartElements(child, child.element.name, elements);
    }
    return;
  }
  for (const child of node.children) {
    appendPartElements(child, child.element.name, elements);
  }
}

function partElement(
  node: SchemaTree,
  name: string,
  repetition: SchemaElement["repetition_type"],
): SchemaElement {
  const element: SchemaElement = {
    ...node.element,
    name,
    repetition_type: repetition,
  };
  if (element.type === "INT96") {
    return {
      ...element,
      type: "INT64",
      logical_type: {
        type: "TIMESTAMP",
        isAdjustedToUTC: false,
        unit: "NANOS",
      },
    };
  }
  const logical = element.logical_type;
  if (isMapLike(node)) {
    return { ...element, converted_type: "MAP" };
  }
  if (isListAnnotated(element)) {
    return { ...element, converted_type: "LIST" };
  }
  if (element.converted_type === undefined && logical?.type === "DECIMAL") {
    return {
      ...element,
      converted_type: "DECIMAL",
      scale: element.scale ?? logical.scale,
      precision: element.precision ?? logical.precision,
    };
  }
  return element;
}

/**
 * The function that rewrites a value of `node`, as hyparquet assembles it,
 * into what hyparquet-writer takes for the node's part schema; undefined
 * when nothing below the node changes.
 */
function partValue(node: SchemaTree): Rewrite | undefined {
  const inner = unrepeatedPartValue(node);
  if (node.element.repetition_type !== "REPEATED") {
    return inner;
  }
  // hyparquet gives null for an empty repeated primitive; the list is
  // required, so it is empty.
  return eachElement(inner, (list) => list ?? []);
}

function unrepeatedPartValue(node: SchemaTree): Rewrite | undefined {
  if (
    node.children.length === 0 ||
    node.element.logical_type?.type === "VARIANT"
  ) {
    return undefined;
  }
  if (isMapLike(node)) {
    const [key, value] = node.children[0].children;
    const parseKey = mapKeyParser(key.element);
    const rewriteValue = partValue(value);
    if (parseKey === undefined && rewriteValue === undefined) {
      return undefined;
    }
    return (map) =>
      map === null || map === undefined
        ? map
        : new Map(
            Object.entries(map as Record<string, unknown>).map(
              ([entryKey, entryValue]) => [
                parseKey === undefined ? entryKey : parseKey(entryKey),
                rewriteValue === undefined
                  ? entryValue
                  : rewriteValue(entryValue),
              ],
            ),
          );
  }
  if (isListWhoseRepeatedFieldIsElement(node)) {
    const [repeated] = node.children;
    if (repeated.children.length === 0) {
      // hyparquet gives null for an empty two-level list and undefined for
      // a null one.
      return (list) => (list === null ? [] : list);
    }
    // hyparquet assembles the group as a struct holding the repeated field.
    const name = repeated.element.name;
    return eachElement(unrepeatedPartValue(repeated), (group) =>
      group === null || group === undefined
        ? group
        : (group as Record<string, unknown>)[name],
    );
  }
  if (isListLike(node)) {
    // The repeated group holds one field, the element.
    const [repeated] = node.children;
    const rewrite = partValue(repeated.children[0]);
    return rewrite === undefined ? undefined : eachElement(rewrite);
  }
  const children = node.children.flatMap((child) => {
    const rewrite = partValue(child);
    return rewrite === undefined ? [] : [{ name: child.element.name, rewrite }];
  });
  if (children.length === 0) {
    return undefined;
  }
  return (struct) => {
    if (struct === null || struct === undefined) {
      return struct;
    }
    const rewritten = { ...(struct as Record<string, unknown>) };
    for (const { name, rewrite } of children) {
      rewritten[name] = rewrite(rewritten[name]);
    }
    return rewritten;
  };
}

/**
 * Applies `rewrite` to each element of a list, after `toList` turns the
 * value hyparquet assembles into the list; undefined when neither changes
 * anything.
 */
function eachElement(
  rewrite: Rewrite | undefined,
  toList?: Rewrite,
): Rewrite | undefined {
  if (rewrite === undefined) {
    return toList;
  }
  return (value) => {
    const list = toList === undefined ? value : toList(value);
    return list === null || list === undefined
      ? list
      : Array.from(list as ArrayLike<unknown>, rewrite);
  };
}
